import { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Plus, Loader2, X, FileDown, FileText, Star, Trash2, Edit2, Image as ImageIcon } from "lucide-react";
import { toast } from "sonner";

const FONTS = [
  { value: "helvetica", label: "Helvetica / Arial" },
  { value: "times", label: "Times New Roman" },
  { value: "courier", label: "Courier" },
];
const PAGE_SIZES = [
  { value: "carta", label: "Carta (21.6 × 27.9 cm)" },
  { value: "oficio", label: "Oficio (21.6 × 34 cm)" },
  { value: "a4", label: "A4 (21 × 29.7 cm)" },
];
const FOLIO_POSITIONS = [
  { value: "top_right", label: "Arriba derecha" },
  { value: "top_left", label: "Arriba izquierda" },
  { value: "footer", label: "Pie de página" },
];

const EMPTY = {
  name: "", logo: "", razon_social: "", nombre_comercial: "", rfc: "", domicilio: "",
  telefono: "", correo: "", sitio_web: "", color_primary: "#1e3a8a", color_secondary: "#64748b",
  font: "helvetica", header_text: "", footer_text: "",
  margins: { top: 20, bottom: 20, left: 20, right: 20 },
  page_size: "carta", folio_position: "top_right", folio_example: "",
  representative_name: "", representative_position: "",
};

function Preview({ f }) {
  const fontFamily = f.font === "times" ? "Georgia, serif" : f.font === "courier" ? "monospace" : "Arial, sans-serif";
  return (
    <div className="bg-white rounded-sm p-4 text-black text-[9px] leading-tight h-full overflow-hidden" style={{ fontFamily }} data-testid="lh-live-preview">
      <div className="flex items-start justify-between gap-2">
        {f.logo ? <img src={f.logo} alt="logo" className="h-10 object-contain" /> : <div className="h-10 w-16 bg-slate-100 flex items-center justify-center text-slate-400 text-[8px]">LOGO</div>}
        <div className="text-right">
          <div className="font-bold text-[11px]" style={{ color: f.color_primary }}>{f.razon_social || "RAZÓN SOCIAL"}</div>
          {f.nombre_comercial && <div style={{ color: f.color_secondary }}>{f.nombre_comercial}</div>}
          {f.rfc && <div style={{ color: f.color_secondary }}>RFC: {f.rfc}</div>}
          {f.domicilio && <div style={{ color: f.color_secondary }}>{f.domicilio}</div>}
          <div style={{ color: f.color_secondary }}>{[f.telefono, f.correo, f.sitio_web].filter(Boolean).join(" · ")}</div>
        </div>
      </div>
      {f.header_text && <div className="text-center mt-1" style={{ color: f.color_secondary }}>{f.header_text}</div>}
      <div className="mt-1 border-b-2" style={{ borderColor: f.color_primary }} />
      {f.folio_position !== "footer" && (
        <div className={`mt-1 font-bold ${f.folio_position === "top_left" ? "text-left" : "text-right"}`} style={{ color: f.color_primary }}>
          {f.folio_example || "FOLIO: EMPRESA/DEP/2026/001"}
        </div>
      )}
      <div className="mt-2 space-y-1 text-[8.5px] text-slate-700">
        <p>ASUNTO: Vista previa de membrete.</p>
        <p>Por medio del presente se muestra un ejemplo de documento emitido con la identidad documental configurada…</p>
      </div>
      {f.representative_name && (
        <div className="mt-3 text-center">
          <div>ATENTAMENTE</div>
          <div className="mt-3 border-t border-black w-28 mx-auto" />
          <div className="font-bold">{f.representative_name}</div>
          <div style={{ color: f.color_secondary }}>{f.representative_position}</div>
        </div>
      )}
      <div className="mt-3 border-t pt-0.5 text-center" style={{ borderColor: f.color_primary, color: f.color_secondary }}>
        {f.footer_text || "Pie de página"}{f.folio_position === "footer" && <span style={{ color: f.color_primary }}> · {f.folio_example || "FOLIO: …/001"}</span>}
      </div>
    </div>
  );
}

export default function Letterheads() {
  const { user } = useAuth();
  const canDelete = ["admin", "contador"].includes(user?.role);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(null); // {editing?, form}
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/legal/letterheads");
      setItems(data);
    } catch { toast.error("No se pudieron cargar los membretes"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const onLogo = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 500 * 1024) { toast.error("Logo muy grande (máx 500KB)"); e.target.value = ""; return; }
    const reader = new FileReader();
    reader.onload = () => setModal((m) => ({ ...m, form: { ...m.form, logo: reader.result } }));
    reader.readAsDataURL(file);
  };

  const save = async () => {
    if (!modal.form.name.trim()) return toast.error("El nombre del diseño es obligatorio");
    setSaving(true);
    try {
      if (modal.editing) await api.patch(`/legal/letterheads/${modal.editing}`, modal.form);
      else await api.post("/legal/letterheads", modal.form);
      toast.success("Membrete guardado");
      setModal(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al guardar"); }
    finally { setSaving(false); }
  };

  const openFile = async (id, kind) => {
    try {
      const res = await api.get(`/legal/letterheads/${id}/preview.${kind}`, { responseType: "blob" });
      const type = kind === "pdf" ? "application/pdf" : "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
      const url = URL.createObjectURL(new Blob([res.data], { type }));
      if (kind === "pdf") window.open(url, "_blank");
      else { const a = document.createElement("a"); a.href = url; a.download = "membrete_preview.docx"; a.click(); }
    } catch { toast.error("No se pudo generar la vista previa"); }
  };

  const setDefault = async (lh) => {
    try { await api.post(`/legal/letterheads/${lh.id}/set-default`); toast.success(`"${lh.name}" es ahora el predeterminado`); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "Error"); }
  };

  const remove = async (lh) => {
    if (!confirm(`¿Eliminar el diseño "${lh.name}"?`)) return;
    try { await api.delete(`/legal/letterheads/${lh.id}`); toast.success("Membrete eliminado"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "No se pudo eliminar"); }
  };

  const setF = (k, v) => setModal((m) => ({ ...m, form: { ...m.form, [k]: v } }));
  const setMargin = (k, v) => setModal((m) => ({ ...m, form: { ...m.form, margins: { ...m.form.margins, [k]: Number(v) || 0 } } }));

  if (loading) return <div className="p-8 text-center text-slate-400">Cargando…</div>;

  return (
    <div className="space-y-4" data-testid="legal-letterheads-tab">
      <button data-testid="lh-new-btn" onClick={() => setModal({ form: { ...EMPTY } })}
        className="btn-primary px-3 py-2 text-xs uppercase tracking-widest flex items-center gap-2">
        <Plus className="w-4 h-4" /> Nuevo membrete
      </button>
      {items.length === 0 && <div className="text-sm text-slate-500 italic">Sin diseños de membrete. Crea el primero — será el predeterminado para oficios y documentos.</div>}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {items.map((lh) => (
          <div key={lh.id} className="panel p-4 space-y-2" data-testid={`lh-card-${lh.id}`}>
            <div className="flex items-center justify-between">
              <div className="font-display font-bold text-slate-100 text-sm">{lh.name}</div>
              {lh.is_default && <span className="text-[10px] px-2 py-0.5 rounded-sm font-mono uppercase text-amber-300 bg-amber-400/10 flex items-center gap-1"><Star className="w-3 h-3" /> Predeterminado</span>}
            </div>
            <div className="flex items-center gap-2">
              {lh.logo ? <img src={lh.logo} alt="logo" className="h-8 object-contain bg-white rounded-sm p-0.5" /> : <ImageIcon className="w-6 h-6 text-slate-600" />}
              <div className="flex gap-1">
                <span className="w-4 h-4 rounded-sm border border-slate-600" style={{ background: lh.color_primary }} title={lh.color_primary} />
                <span className="w-4 h-4 rounded-sm border border-slate-600" style={{ background: lh.color_secondary }} title={lh.color_secondary} />
              </div>
              <span className="text-[10px] font-mono text-slate-500 uppercase">{lh.page_size} · {FONTS.find((f) => f.value === lh.font)?.label.split(" ")[0]}</span>
            </div>
            <div className="text-xs text-slate-400 truncate">{lh.razon_social || "—"}</div>
            <div className="flex gap-1.5 flex-wrap pt-1">
              <button data-testid={`lh-edit-${lh.id}`} onClick={() => setModal({ editing: lh.id, form: { ...EMPTY, ...lh } })} className="btn-ghost px-2 py-1 text-xs flex items-center gap-1"><Edit2 className="w-3 h-3" /> Editar</button>
              <button data-testid={`lh-pdf-${lh.id}`} onClick={() => openFile(lh.id, "pdf")} className="btn-ghost px-2 py-1 text-xs flex items-center gap-1" style={{ borderColor: "var(--cyan)", color: "var(--cyan)" }}><FileDown className="w-3 h-3" /> PDF</button>
              <button data-testid={`lh-word-${lh.id}`} onClick={() => openFile(lh.id, "docx")} className="btn-ghost px-2 py-1 text-xs flex items-center gap-1" style={{ borderColor: "#60a5fa", color: "#93c5fd" }}><FileText className="w-3 h-3" /> Word</button>
              {!lh.is_default && (
                <>
                  <button data-testid={`lh-default-${lh.id}`} onClick={() => setDefault(lh)} className="btn-ghost px-2 py-1 text-xs flex items-center gap-1" style={{ borderColor: "#fbbf24", color: "#fcd34d" }}><Star className="w-3 h-3" /> Predet.</button>
                  {canDelete && <button data-testid={`lh-delete-${lh.id}`} onClick={() => remove(lh)} className="btn-ghost px-2 py-1 text-xs" style={{ borderColor: "#dc2626", color: "#fca5a5" }}><Trash2 className="w-3 h-3" /></button>}
                </>
              )}
            </div>
          </div>
        ))}
      </div>

      {modal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="panel w-full max-w-5xl p-5 max-h-[92vh] overflow-y-auto" data-testid="lh-modal">
            <div className="flex items-center justify-between mb-4">
              <div className="font-display font-bold text-slate-100">{modal.editing ? "Editar membrete" : "Nuevo membrete"}</div>
              <button onClick={() => setModal(null)}><X className="w-4 h-4 text-slate-400" /></button>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
              <div className="lg:col-span-3 space-y-3">
                <div>
                  <label className="label-mono block mb-1">Nombre del diseño *</label>
                  <input data-testid="lh-form-name" value={modal.form.name} onChange={(e) => setF("name", e.target.value)} className="w-full input-cyber" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {[["razon_social", "Razón social"], ["nombre_comercial", "Nombre comercial"], ["rfc", "RFC"], ["telefono", "Teléfono"],
                    ["correo", "Correo"], ["sitio_web", "Sitio web"]].map(([k, l]) => (
                    <div key={k}>
                      <label className="label-mono block mb-1">{l}</label>
                      <input data-testid={`lh-form-${k}`} value={modal.form[k]} onChange={(e) => setF(k, e.target.value)} className="w-full input-cyber" />
                    </div>
                  ))}
                </div>
                <div>
                  <label className="label-mono block mb-1">Domicilio</label>
                  <input value={modal.form.domicilio} onChange={(e) => setF("domicilio", e.target.value)} className="w-full input-cyber" />
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <div>
                    <label className="label-mono block mb-1">Color primario</label>
                    <input data-testid="lh-form-color-primary" type="color" value={modal.form.color_primary} onChange={(e) => setF("color_primary", e.target.value)} className="w-full h-9 bg-transparent cursor-pointer" />
                  </div>
                  <div>
                    <label className="label-mono block mb-1">Color secundario</label>
                    <input type="color" value={modal.form.color_secondary} onChange={(e) => setF("color_secondary", e.target.value)} className="w-full h-9 bg-transparent cursor-pointer" />
                  </div>
                  <div>
                    <label className="label-mono block mb-1">Tipografía</label>
                    <select data-testid="lh-form-font" value={modal.form.font} onChange={(e) => setF("font", e.target.value)} className="w-full input-cyber">
                      {FONTS.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="label-mono block mb-1">Tamaño de hoja</label>
                    <select data-testid="lh-form-page-size" value={modal.form.page_size} onChange={(e) => setF("page_size", e.target.value)} className="w-full input-cyber">
                      {PAGE_SIZES.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="label-mono block mb-1">Posición del folio</label>
                    <select data-testid="lh-form-folio-position" value={modal.form.folio_position} onChange={(e) => setF("folio_position", e.target.value)} className="w-full input-cyber">
                      {FOLIO_POSITIONS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="label-mono block mb-1">Ejemplo de folio</label>
                    <input placeholder="INCONTI/IGIFE/2026/001" value={modal.form.folio_example} onChange={(e) => setF("folio_example", e.target.value)} className="w-full input-cyber" />
                  </div>
                </div>
                <div className="grid grid-cols-4 gap-2">
                  {[["top", "Margen sup. (mm)"], ["bottom", "Inf. (mm)"], ["left", "Izq. (mm)"], ["right", "Der. (mm)"]].map(([k, l]) => (
                    <div key={k}>
                      <label className="label-mono block mb-1">{l}</label>
                      <input type="number" min="5" max="60" value={modal.form.margins[k]} onChange={(e) => setMargin(k, e.target.value)} className="w-full input-cyber" />
                    </div>
                  ))}
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="label-mono block mb-1">Texto de encabezado</label>
                    <input value={modal.form.header_text} onChange={(e) => setF("header_text", e.target.value)} className="w-full input-cyber" />
                  </div>
                  <div>
                    <label className="label-mono block mb-1">Pie de página</label>
                    <input data-testid="lh-form-footer" value={modal.form.footer_text} onChange={(e) => setF("footer_text", e.target.value)} className="w-full input-cyber" />
                  </div>
                  <div>
                    <label className="label-mono block mb-1">Representante legal</label>
                    <input value={modal.form.representative_name} onChange={(e) => setF("representative_name", e.target.value)} className="w-full input-cyber" />
                  </div>
                  <div>
                    <label className="label-mono block mb-1">Cargo del representante</label>
                    <input value={modal.form.representative_position} onChange={(e) => setF("representative_position", e.target.value)} className="w-full input-cyber" />
                  </div>
                </div>
                <div>
                  <label className="label-mono block mb-1">Logotipo (PNG/JPG, máx 500KB)</label>
                  <div className="flex items-center gap-3">
                    <input data-testid="lh-form-logo" type="file" accept="image/png,image/jpeg,image/webp" onChange={onLogo} className="text-xs text-slate-300" />
                    {modal.form.logo && <button onClick={() => setF("logo", "")} className="text-xs text-red-300 hover:underline">Quitar logo</button>}
                  </div>
                </div>
                <button data-testid="lh-save-btn" onClick={save} disabled={saving}
                  className="btn-primary w-full py-2 text-xs uppercase tracking-widest flex items-center justify-center gap-2">
                  {saving && <Loader2 className="w-4 h-4 animate-spin" />} Guardar membrete
                </button>
              </div>
              <div className="lg:col-span-2">
                <div className="label-mono mb-2">// VISTA PREVIA EN VIVO</div>
                <Preview f={modal.form} />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
